import React, { useRef, useState } from 'react';
import { useDocuments } from './hooks/useDocuments';
import { DOCUMENTS_PER_PAGE } from './hooks/vaultQueryParams';
import { ProgressOverlay } from '../../common/progress-overlay/ProgressOverlay';
import styles from './scss/DocumentVault.module.scss';
import { Box, Grid, Pagination, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import UploadButton from '../../common/upload-button/UploadButton';
import FormUpload from '../../common/form-upload/FormUpload';
import DocumentListVault from './components/document-list-vault/DocumentListVault';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { useTranslation } from 'react-i18next';
import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import { selectBusinessProfileContacts } from '../../slices/business-profile/businessProfileSlice';
import DashboardIcon from '../../../assets/svg/dashboard.svg?react';
import BallotIcon from '../../../assets/svg/ballot.svg?react';
import { CardViewContent } from './components/card-view-content/CardViewContent';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import PostAddIcon from '@mui/icons-material/PostAdd';
import Preview from './components/preview-block/Preview';
import PreviewTooltip from '../../common/preview-block/PreviewTooltip';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { showOverlay } from '../../slices/show-progress-overlay/showProgressOverlay';
import { DocumentVaultItemModel } from '../../models/document.vault.item.model';
import { Document } from './components/document-list-vault/types';
import { RootState } from '../../store';
import { AssignProfilePersonModal } from '../../common/assign-profile-person-modal/AssignProfilePersonModal';
import { useDocumentSigning } from './hooks/useDocumentSigning';
import { useAssignProfilePersonModal } from '../../common/assign-profile-person-modal/useAssignProfilePersonModal';
import { ROUTES } from '../../common/constants/routes';
import BlankDocumentModal from '../../common/blank-document-modal';

/** Same breakpoints as `breakpoints.$tablet-mobile` / `$mobile` in scss/settings/_breakpoints.scss. */
const TABLET_MOBILE_QUERY = '(max-width: 991px)';
const MOBILE_QUERY = '(max-width: 767px)';

const DocumentVaultBlock = () => {
  /********* To refactor **************/
  const { t } = useTranslation();
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const showProgressOverlay = useAppSelector(
    (state: RootState) => state.showProgressOverlay.showOverlay
  );
  const documentsLoading = useAppSelector((state: RootState) => state.documents.status);
  const businessContacts = useAppSelector(selectBusinessProfileContacts);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isUploadAndSignOpen, setIsUploadAndSignOpen] = useState(false);
  const [isBlankModalOpen, setIsBlankModalOpen] = useState(false);
  const formUploadRef = useRef<HTMLDivElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const {
    document: documentToSign,
    setDocument: setDocumentToSign,
    handleSignOriginalDocument,
    handleSignLatestVersion,
    setOpenAssignPersonDialog,
    openAssignPersonDialog,
  } = useDocumentSigning();
  const { patchDocumentWithProfilePerson, setAssignee, assignee } = useAssignProfilePersonModal(
    documentToSign?.category,
    documentToSign
  );
  const handleUploadClose = () => {
    setIsUploadOpen(false);
  };

  const handleUploadOpen = () => {
    setIsUploadOpen(true);
  };

  const handleUploadAndSignClose = () => {
    setIsUploadAndSignOpen(false);
  };

  const handleUploadAndSignOpen = () => {
    setIsUploadAndSignOpen(true);
  };

  const dispatch = useAppDispatch();

  /*const checkProfilePersonsAvailability = (category: string) => {
    switch (category) {
      case 'employment':
        return employees?.length > 0;
      case 'business':
        return (businessContacts?.length ?? 0) > 0;
      case 'customers':
        return customers?.length > 0;
      case 'suppliers':
        return suppliers?.length > 0;
    }
  };*/

  const handleUploadAndSignSuccess = (uploadedDocument: DocumentVaultItemModel, tab?: Window) => {
    const document = uploadedDocument as Document;
    /*if (
      document.customers.length === 0 &&
      document.employees.length === 0 &&
      document.suppliers.length === 0 &&
      (document.business_profile_contacts?.length ?? 0) === 0 &&
      checkProfilePersonsAvailability(document.category)
    ) {
      dispatch(hideOverlay());
      if (tab) {
        tab.close();
      }
      setDocumentToSign(document);
      setOpenAssignPersonDialog(true);
    } else {*/
    handleSignOriginalDocument(document, undefined, undefined, tab);
    //}
  };

  const {
    documents,
    documentContractsLoading,
    documentUploadsLoading,
    customersLoading,
    suppliersLoading,
    employeesLoading,
    businessProfileLoading,
    documentsCount,
    documentsPage,
    getRequestedPage,
    handleTabChange,
    activeTab,
    setActiveTab,
    cardView,
    setCardView,
  } = useDocuments();
  const totalPages = Math.ceil(documentsCount / DOCUMENTS_PER_PAGE);
  const isTabletMobile = useMediaQuery(TABLET_MOBILE_QUERY);
  const isMobile = useMediaQuery(MOBILE_QUERY);

  // Handle page change: the page is written to the URL (?page=N) and the documents are fetched from it
  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    getRequestedPage(page);
  };

  /********* To refactor later*************/

  const profilesLoading =
    customersLoading === 'loading' ||
    suppliersLoading === 'loading' ||
    employeesLoading === 'loading' ||
    businessProfileLoading === 'loading';
  const showPagination = documents.length > 0 && (cardView ? activeTab !== 'All' : true);
  const blockTitle =
    activeTab === 'All'
      ? t('documentVault.allDocuments')
      : t('documentVault.categoryDocuments', {
          category: t(`tagsLabels.categories.${activeTab.toLowerCase()}`),
        });

  // Aside (mockup): upload / eSigning / new document actions, the categories and the
  // "How the Vault works" overview. Tablet / mobile: it goes above the documents block.
  const renderAside = () => (
    <Box component='aside' id='documentVaultAside' className={styles.aside}>
      <Box className={styles.aside_actions}>
        <Box className={styles.aside_action}>
          <UploadButton
            id='documentVaultBlockNewDocumentBtn'
            onClick={() => setIsBlankModalOpen(true)}
            buttonText={t('buttons.newDocument')}
            isArrowDown={false}
            icon={PostAddIcon}
          />
        </Box>
        <Box ref={uploadButtonRef} className={styles.aside_action}>
          <UploadButton
            id='documentVaultBlockUploadFilesBtn'
            disabled={profilesLoading}
            onClick={handleUploadOpen}
            buttonText={t('buttons.uploadFiles')}
            isArrowDown={false}
          />
          {isUploadOpen && (
            <Box
              className={styles.form_box}
              ref={formUploadRef}
              style={{ alignSelf: 'flex-start' }}
            >
              <FormUpload
                employees={employees}
                suppliers={suppliers}
                customers={customers}
                businessContacts={businessContacts ?? undefined}
                categoriesAsOption={true}
                onCancel={handleUploadClose}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                categoryValue={activeTab === 'All' ? '' : activeTab.toLowerCase()}
                setActiveTab={setActiveTab}
              />
            </Box>
          )}
        </Box>
        <Box className={styles.aside_action}>
          <UploadButton
            id='documentVaultBlockUploadAndSignPdfBtn'
            disabled={profilesLoading}
            onClick={handleUploadAndSignOpen}
            buttonText={t('buttons.uploadAndSignPDF')}
            isArrowDown={false}
            icon={EditNoteOutlinedIcon}
          />
          {isUploadAndSignOpen && (
            <Box className={styles.form_box} style={{ alignSelf: 'flex-start' }}>
              <FormUpload
                employees={employees}
                suppliers={suppliers}
                customers={customers}
                businessContacts={businessContacts ?? undefined}
                categoriesAsOption={true}
                uploadWithSigning={true}
                onCancel={handleUploadAndSignClose}
                /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
                // @ts-ignore
                categoryValue={activeTab === 'All' ? '' : activeTab.toLowerCase()}
                setActiveTab={setActiveTab}
                accept='.pdf'
                supportedFormatsKey='messages.supportedFormatsPDF'
                maxFiles={1}
                onUploadSuccess={handleUploadAndSignSuccess}
              />
            </Box>
          )}
        </Box>
      </Box>
      <Box className={styles.aside_divider} />
      <Box className={styles.aside_section}>
        <Typography id='documentVaultCategoryLabel' component='h2' className={styles.aside_label}>
          {t('labels.category')}
        </Typography>
        <Tabs
          orientation={isTabletMobile ? 'horizontal' : 'vertical'}
          variant='scrollable'
          scrollButtons={isTabletMobile ? 'auto' : false}
          allowScrollButtonsMobile
          value={activeTab}
          onChange={(_, newValue) => {
            handleTabChange(newValue);
          }}
          aria-label={t('labels.documentCategories')}
          className={styles.category_tabs}
        >
          <Tab id='documentVaultBlockAllTab' label={t('labels.all')} value='All' />
          <Tab
            id='documentVaultBlockBusinessTab'
            label={t('tagsLabels.categories.business')}
            value='Business'
          />
          <Tab
            id='documentVaultBlockEmploymentTab'
            label={t('tagsLabels.categories.employment')}
            value='Employment'
          />
          <Tab
            id='documentVaultBlockCustomersTab'
            label={t('tagsLabels.categories.customers')}
            value='Customers'
          />
          <Tab
            id='documentVaultBlockSuppliersTab'
            label={t('tagsLabels.categories.suppliers')}
            value='Suppliers'
          />
        </Tabs>
      </Box>
      <PreviewTooltip title={<Preview />} arrow placement='right'>
        <button id='documentVaultHowItWorksBtn' type='button' className={styles.aside_help}>
          <InfoOutlinedIcon fontSize='inherit' className={styles.aside_help_icon} />
          {t('documentVault.howVaultWorks')}
        </button>
      </PreviewTooltip>
    </Box>
  );

  // Pagination: "Page X of Y" and the page buttons, shown as before (list view, or a category in
  // the card view). It sits in the block header, right before the card / list view switcher.
  const renderPagination = () =>
    showPagination && (
      <Box id='documentVaultBlockPaginationBox' className={styles.header_pagination}>
        <Typography className={styles.pages_value} variant='caption'>
          {t('labels.pageOfTotal', { page: documentsPage, total: totalPages })}
        </Typography>
        <Pagination
          id='documentVaultBlockPagePagination'
          disabled={documentsLoading === 'loading'}
          count={totalPages}
          page={documentsPage}
          onChange={handlePageChange}
          siblingCount={isMobile ? 0 : 1}
          boundaryCount={1}
          className={styles.pagination}
        />
      </Box>
    );

  // Block header: the category title, then the pagination and the card / list view switcher.
  const renderHeader = () => (
    <Box id='documentVaultBlockHeader' className={styles.block_header}>
      <Typography id='documentVaultBlockTitle' component='h2' className={styles.block_title}>
        {blockTitle}
      </Typography>
      <Box className={styles.header_controls}>
        {renderPagination()}
        <Box
          className={styles.card_list_view_container}
          role='group'
          aria-label={t('documentVault.vaultView')}
        >
          <Tooltip title={t('buttons.card')} placement='top' arrow>
            <button
              id='documentVaultBlockCardBtn'
              type='button'
              aria-pressed={cardView}
              className={`${styles.variant_btn}${cardView ? ` ${styles.variant_btn_active}` : ''}`}
              onClick={() => setCardView(true)}
            >
              <DashboardIcon id='documentVaultBlockCardBtnDashboardIcon' />
              <span id='documentVaultBlockCardBtnText' className={styles.visually_hidden}>
                {t('buttons.card')}
              </span>
            </button>
          </Tooltip>
          <Tooltip title={t('buttons.list')} placement='top' arrow>
            <button
              id='documentVaultBlockListBtn'
              type='button'
              aria-pressed={!cardView}
              onClick={() => {
                setCardView(false);
              }}
              className={`${styles.variant_btn}${!cardView ? ` ${styles.variant_btn_active}` : ''}`}
            >
              <BallotIcon id='documentVaultBlockListBtnBallotIcon' />
              <span id='documentVaultBlockListBtnText' className={styles.visually_hidden}>
                {t('buttons.list')}
              </span>
            </button>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Grid component='div' container spacing={3} sx={{ marginTop: '16px' }}>
        <Grid className={'controls-grid'} component='div' size={{ xs: 12 }}>
          <Box className={styles.page_description}>{t('messages.documentVaultDescription')}</Box>
          {/* Desktop: the aside sits to the left of the documents block (header with the
              pagination + table / cards). Tablet / mobile: it goes above it. */}
          <Box id='documentVaultLayout' className={styles.vault_layout}>
            {renderAside()}
            <Box
              id='documentVaultBlock'
              className={`${styles.vault_block}${cardView ? '' : ` ${styles.vault_block_joined}`}`}
            >
              {renderHeader()}
              <Box className={styles.vault_block_content}>
                {cardView ? (
                  <CardViewContent
                    activeTab={activeTab}
                    changeTabFunction={handleTabChange}
                    documents={documents as Document[]}
                    documentsCount={documentsCount}
                    getRequestedPage={getRequestedPage}
                  />
                ) : (
                  <DocumentListVault documents={documents as Document[]} compact embedded />
                )}
                {(documentUploadsLoading === 'loading' ||
                  documentContractsLoading === 'loading' ||
                  profilesLoading) &&
                  !showProgressOverlay && <ProgressOverlay position='absolute' />}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      {isBlankModalOpen && (
        <BlankDocumentModal
          isBlankModalOpen={isBlankModalOpen}
          onClose={() => setIsBlankModalOpen(false)}
        />
      )}
      {openAssignPersonDialog && documentToSign && (
        <AssignProfilePersonModal
          open={openAssignPersonDialog}
          onClose={() => {
            setTimeout(() => {
              setOpenAssignPersonDialog(false);
              setDocumentToSign(null as unknown as Document);
              setAssignee('');
            }, 100);
          }}
          modalTitle={t('headers.startSigning')}
          category={documentToSign?.category}
          document={documentToSign}
          goBackButtonText={t('buttons.goBack')}
          modalMessage={t('messages.signingRecommendations')}
          assignee={assignee}
          setAssignee={setAssignee}
          secondaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            setOpenAssignPersonDialog(false);
            if (assignee && assignee !== 'noAssignee') {
              if (documentToSign?.category === 'suppliers') {
                handleSignOriginalDocument(
                  documentToSign,
                  Number(assignee),
                  true,
                  // @ts-expect-error: there can be an error
                  tab
                );
                setAssignee('');
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => {
                    handleSignOriginalDocument(
                      res,
                      undefined,
                      undefined,
                      // @ts-expect-error: there can be an error
                      tab
                    );
                    setAssignee('');
                  })
                  .catch(() => {
                    handleSignOriginalDocument(
                      documentToSign,
                      undefined,
                      true,
                      // @ts-expect-error: there can be an error
                      tab
                    );
                    setAssignee('');
                  });
              }
            } else {
              // @ts-expect-error: there can be an error
              handleSignOriginalDocument(documentToSign, undefined, true, tab);
              setAssignee('');
            }
          }}
          primaryAction={() => {
            dispatch(showOverlay());
            const url = `${window.location.origin}${ROUTES.PROCESSING}`;
            const tab = window.open('', '_blank');
            // @ts-expect-error: there can be an error
            tab.location.href = url;
            setOpenAssignPersonDialog(false);
            if (assignee && assignee !== 'noAssignee') {
              if (documentToSign?.category === 'suppliers') {
                handleSignLatestVersion(
                  documentToSign,
                  undefined,
                  Number(assignee),
                  'contract_generation' in documentToSign,
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  tab
                );
              } else {
                patchDocumentWithProfilePerson()
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  //@ts-ignore
                  .then((res) => {
                    handleSignLatestVersion(
                      res,
                      undefined,
                      undefined,
                      'contract_generation' in documentToSign,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      tab
                    );
                    setAssignee('');
                  })
                  .catch(() => {
                    handleSignLatestVersion(
                      documentToSign,
                      undefined,
                      undefined,
                      'contract_generation' in documentToSign,
                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                      //@ts-ignore
                      tab
                    );
                    setAssignee('');
                  });
              }
            } else {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              handleSignLatestVersion(documentToSign, undefined, undefined, true, tab);
              setAssignee('');
            }
          }}
        />
      )}
    </>
  );
};

export default DocumentVaultBlock;
